import {
  Analytics,
  Close,
  ContentCopy,
  Description,
  Edit,
  ExpandMore,
  Folder,
  Launch,
  MouseOutlined,
  Note,
  TouchApp,
  Visibility,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { DataGrid, GridRowParams } from '@mui/x-data-grid';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { CardTitle } from './card-title';
import {
  useGetEncounterTelemetryQuery,
  useGetEncounterTelemetryAnalyticsQuery,
} from '../store/api';
import { formatDateTime } from '../utils/formatting';

interface EncounterTelemetryCardProps {
  encounterId?: string;
  noteId?: string;
}

interface RawTelemetryEvent {
  type?: string;
  [key: string]: any;
}

// Helper function to clean section titles and IDs for display
// Normalizes the two different section_id formats we receive and removes volatile numeric suffixes:
// 1) Clean slug: "clinical-reasoning-medical-decision-making-mdm-4" -> "clinical-reasoning-medical-decision-making-mdm"
// 2) Full jotdoc: "jotdoc:<uuid>:section:<slug>" -> "<slug>" (without trailing number)
//    Full jotdoc heading: "jotdoc:<uuid>:h2:5" -> "h2"
function cleanSectionDisplay(section: { section_title?: string; section_id?: string }) {
  const title = section.section_title || 'Untitled Section';
  const id = section.section_id || '';

  // Clean the section_id for display
  let displayId = id;

  if (id.startsWith('jotdoc:')) {
    const parts = id.split(':');
    if (parts.length >= 4 && parts[2] === 'section') {
      // jotdoc:<uuid>:section:<slug(-n)?> -> use slug
      displayId = parts.slice(3).join(':');
    } else if (parts.length >= 3) {
      // jotdoc:<uuid>:h2:5 -> keep heading level only (e.g., "h2")
      displayId = parts[2];
    } else {
      // Fallback: just remove prefix
      displayId = id.replace('jotdoc:', '');
    }
  }

  // Remove trailing numeric suffix from slug-like IDs: "...-4" -> "..."
  // Only apply when ID looks like a slug (contains '-')
  if (displayId && displayId.includes('-')) {
    displayId = displayId.replace(/-\d+$/u, '');
  }

  return { title, displayId };
}

function formatHoverDuration(ms: number): string {
  if (ms < 1000) {
    return `${Math.round(ms)}ms`;
  }
  const seconds = ms / 1000;
  if (seconds < 60) {
    return `${seconds.toFixed(1)}s`;
  }
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${minutes}m ${remainingSeconds}s`;
}

function SectionInteractedEventCard({ event, index }: { event: RawTelemetryEvent; index: number }) {
  const metadata = event.metadata || {};
  const hoverDuration = event.hoverDuration || 0;
  const interactionCounts = event.interactionCounts || { clicks: 0, copies: 0, selects: 0 };

  return (
    <Card className="shadow-sm border border-gray-200">
      <CardContent className="p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className="h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center">
              <TouchApp className="h-4 w-4 text-blue-600" />
            </div>
            <Chip label={`#${index + 1}`} size="small" variant="outlined" className="text-xs h-5" />
            <Chip
              label={event.type || 'section_interacted'}
              size="small"
              color="primary"
              className="text-xs h-5"
            />
            {metadata.section_title && (
              <span className="text-sm font-semibold text-gray-900">{metadata.section_title}</span>
            )}
          </div>
          {event.received_at && (
            <span className="text-xs text-gray-500">{formatDateTime(event.received_at)}</span>
          )}
        </div>

        <div className="grid grid-cols-4 gap-2">
          {/* Hover Duration */}
          <Box className="p-2 bg-blue-50 rounded border border-blue-200">
            <div className="flex items-center gap-1 mb-1">
              <TouchApp className="h-3 w-3 text-blue-600" />
              <Typography variant="caption" className="text-blue-900 font-medium text-xs">
                Hover
              </Typography>
            </div>
            <Typography variant="body2" className="text-blue-600 font-bold">
              {formatHoverDuration(hoverDuration)}
            </Typography>
          </Box>

          {/* Clicks */}
          <Box className="p-2 bg-gray-50 rounded border border-gray-200">
            <div className="flex items-center gap-1 mb-1">
              <MouseOutlined className="h-3 w-3 text-gray-600" />
              <Typography variant="caption" className="text-gray-600 font-medium text-xs">
                Clicks
              </Typography>
            </div>
            <Typography variant="body2" className="text-gray-900 font-bold">
              {interactionCounts.clicks || 0}
            </Typography>
          </Box>

          {/* Copies */}
          <Box className="p-2 bg-gray-50 rounded border border-gray-200">
            <div className="flex items-center gap-1 mb-1">
              <ContentCopy className="h-3 w-3 text-gray-600" />
              <Typography variant="caption" className="text-gray-600 font-medium text-xs">
                Copies
              </Typography>
            </div>
            <Typography variant="body2" className="text-gray-900 font-bold">
              {interactionCounts.copies || 0}
            </Typography>
          </Box>

          {/* Selects */}
          <Box className="p-2 bg-gray-50 rounded border border-gray-200">
            <div className="flex items-center gap-1 mb-1">
              <TouchApp className="h-3 w-3 text-gray-600" />
              <Typography variant="caption" className="text-gray-600 font-medium text-xs">
                Selects
              </Typography>
            </div>
            <Typography variant="body2" className="text-gray-900 font-bold">
              {interactionCounts.selects || 0}
            </Typography>
          </Box>
        </div>

        {/* Metadata - Compact */}
        {(metadata.section_id || metadata.note_id || event.docId) && (
          <div className="mt-2 pt-2 border-t border-gray-200">
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
              {metadata.section_id && (
                <div>
                  <span className="text-gray-500">Section: </span>
                  <span className="text-gray-900 font-mono">
                    {metadata.section_id.substring(0, 12)}...
                  </span>
                </div>
              )}
              {metadata.note_id && (
                <div>
                  <span className="text-gray-500">Note: </span>
                  <span className="text-gray-900 font-mono">
                    {metadata.note_id.substring(0, 12)}...
                  </span>
                </div>
              )}
              {event.docId && (
                <div>
                  <span className="text-gray-500">Doc: </span>
                  <span className="text-gray-900 font-mono text-xs">
                    {event.docId.substring(0, 15)}...
                  </span>
                </div>
              )}
              {event.docVersion && (
                <div>
                  <span className="text-gray-500">v{event.docVersion}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function TextCopiedEventCard({ event, index }: { event: RawTelemetryEvent; index: number }) {
  const metadata = event.metadata || {};
  const chars = event.chars || { selected: 0, total: 0, percent: 0 };
  const timings = event.timings || {};
  const modality = event.modality || 'unknown';

  return (
    <Card className="shadow-sm border border-gray-200">
      <CardContent className="p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className="h-6 w-6 rounded-full bg-green-100 flex items-center justify-center">
              <ContentCopy className="h-4 w-4 text-green-600" />
            </div>
            <Chip label={`#${index + 1}`} size="small" variant="outlined" className="text-xs h-5" />
            <Chip
              label={event.type || 'text_copied'}
              size="small"
              color="primary"
              className="text-xs h-5"
            />
          </div>
          {event.received_at && (
            <span className="text-xs text-gray-500">{formatDateTime(event.received_at)}</span>
          )}
        </div>

        <div className="grid grid-cols-4 gap-2">
          {/* Characters Copied */}
          <Box className="p-2 bg-green-50 rounded border border-green-200">
            <div className="flex items-center gap-1 mb-1">
              <ContentCopy className="h-3 w-3 text-green-600" />
              <Typography variant="caption" className="text-green-900 font-medium text-xs">
                Chars
              </Typography>
            </div>
            <Typography variant="body2" className="text-green-600 font-bold">
              {chars.selected?.toLocaleString() || 0}
            </Typography>
            <Typography variant="caption" className="text-green-700 text-xs">
              of {chars.total?.toLocaleString() || 0}
            </Typography>
          </Box>

          {/* Percent of Document */}
          <Box className="p-2 bg-blue-50 rounded border border-blue-200">
            <div className="flex items-center gap-1 mb-1">
              <Typography variant="caption" className="text-blue-900 font-medium text-xs">
                Percent
              </Typography>
            </div>
            <Typography variant="body2" className="text-blue-600 font-bold">
              {chars.percent ? `${(chars.percent * 100).toFixed(1)}%` : '0%'}
            </Typography>
          </Box>

          {/* Time to Copy */}
          <Box className="p-2 bg-gray-50 rounded border border-gray-200">
            <div className="flex items-center gap-1 mb-1">
              <Typography variant="caption" className="text-gray-600 font-medium text-xs">
                Copy Time
              </Typography>
            </div>
            <Typography variant="body2" className="text-gray-900 font-bold">
              {timings.msToCopy
                ? timings.msToCopy < 1000
                  ? `${Math.round(timings.msToCopy)}ms`
                  : `${(timings.msToCopy / 1000).toFixed(1)}s`
                : 'N/A'}
            </Typography>
          </Box>

          {/* Modality */}
          <Box className="p-2 bg-gray-50 rounded border border-gray-200">
            <div className="flex items-center gap-1 mb-1">
              {modality === 'mouse' ? (
                <MouseOutlined className="h-3 w-3 text-gray-600" />
              ) : (
                <TouchApp className="h-3 w-3 text-gray-600" />
              )}
              <Typography variant="caption" className="text-gray-600 font-medium text-xs">
                {modality === 'mouse' ? 'Mouse' : modality === 'keyboard' ? 'Keyboard' : 'Unknown'}
              </Typography>
            </div>
            {event.isBackward !== undefined && (
              <Typography variant="caption" className="text-gray-500 text-xs">
                {event.isBackward ? 'Backward' : 'Forward'}
              </Typography>
            )}
          </Box>
        </div>

        {/* Metadata - Compact */}
        {(metadata.section_id || metadata.note_id || event.docId || event.selectionId) && (
          <div className="mt-2 pt-2 border-t border-gray-200">
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
              {metadata.section_id && (
                <div>
                  <span className="text-gray-500">Section: </span>
                  <span className="text-gray-900 font-mono">
                    {metadata.section_id.substring(0, 12)}...
                  </span>
                </div>
              )}
              {metadata.note_id && (
                <div>
                  <span className="text-gray-500">Note: </span>
                  <span className="text-gray-900 font-mono">
                    {metadata.note_id.substring(0, 12)}...
                  </span>
                </div>
              )}
              {event.docId && (
                <div>
                  <span className="text-gray-500">Doc: </span>
                  <span className="text-gray-900 font-mono text-xs">
                    {event.docId.substring(0, 15)}...
                  </span>
                </div>
              )}
              {event.selectionId && (
                <div>
                  <span className="text-gray-500">Selection ID: </span>
                  <span className="text-gray-900 font-mono text-xs">
                    {event.selectionId.substring(0, 12)}...
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function TextSelectedEventCard({ event, index }: { event: RawTelemetryEvent; index: number }) {
  const metadata = event.metadata || {};
  const chars = event.chars || { selected: 0, total: 0, percent: 0 };
  const timings = event.timings || {};
  const modality = event.modality || 'unknown';

  return (
    <Card className="shadow-sm border border-gray-200">
      <CardContent className="p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className="h-6 w-6 rounded-full bg-purple-100 flex items-center justify-center">
              <TouchApp className="h-4 w-4 text-purple-600" />
            </div>
            <Chip label={`#${index + 1}`} size="small" variant="outlined" className="text-xs h-5" />
            <Chip
              label={event.type || 'text_selected'}
              size="small"
              color="primary"
              className="text-xs h-5"
            />
          </div>
          {event.received_at && (
            <span className="text-xs text-gray-500">{formatDateTime(event.received_at)}</span>
          )}
        </div>

        <div className="grid grid-cols-4 gap-2">
          {/* Characters Selected */}
          <Box className="p-2 bg-purple-50 rounded border border-purple-200">
            <div className="flex items-center gap-1 mb-1">
              <TouchApp className="h-3 w-3 text-purple-600" />
              <Typography variant="caption" className="text-purple-900 font-medium text-xs">
                Chars
              </Typography>
            </div>
            <Typography variant="body2" className="text-purple-600 font-bold">
              {chars.selected?.toLocaleString() || 0}
            </Typography>
            <Typography variant="caption" className="text-purple-700 text-xs">
              of {chars.total?.toLocaleString() || 0}
            </Typography>
          </Box>

          {/* Percent of Document */}
          <Box className="p-2 bg-blue-50 rounded border border-blue-200">
            <div className="flex items-center gap-1 mb-1">
              <Typography variant="caption" className="text-blue-900 font-medium text-xs">
                Percent
              </Typography>
            </div>
            <Typography variant="body2" className="text-blue-600 font-bold">
              {chars.percent ? `${(chars.percent * 100).toFixed(1)}%` : '0%'}
            </Typography>
          </Box>

          {/* Time to Settle */}
          <Box className="p-2 bg-gray-50 rounded border border-gray-200">
            <div className="flex items-center gap-1 mb-1">
              <Typography variant="caption" className="text-gray-600 font-medium text-xs">
                Settle Time
              </Typography>
            </div>
            <Typography variant="body2" className="text-gray-900 font-bold">
              {timings.msToSettle
                ? timings.msToSettle < 1000
                  ? `${Math.round(timings.msToSettle)}ms`
                  : `${(timings.msToSettle / 1000).toFixed(1)}s`
                : 'N/A'}
            </Typography>
          </Box>

          {/* Modality */}
          <Box className="p-2 bg-gray-50 rounded border border-gray-200">
            <div className="flex items-center gap-1 mb-1">
              {modality === 'mouse' ? (
                <MouseOutlined className="h-3 w-3 text-gray-600" />
              ) : (
                <TouchApp className="h-3 w-3 text-gray-600" />
              )}
              <Typography variant="caption" className="text-gray-600 font-medium text-xs">
                {modality === 'mouse' ? 'Mouse' : modality === 'keyboard' ? 'Keyboard' : 'Unknown'}
              </Typography>
            </div>
            {event.isBackward !== undefined && (
              <Typography variant="caption" className="text-gray-500 text-xs">
                {event.isBackward ? 'Backward' : 'Forward'}
              </Typography>
            )}
          </Box>
        </div>

        {/* Metadata - Compact */}
        {(metadata.section_id || metadata.note_id || event.docId || event.selectionId) && (
          <div className="mt-2 pt-2 border-t border-gray-200">
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
              {metadata.section_id && (
                <div>
                  <span className="text-gray-500">Section: </span>
                  <span className="text-gray-900 font-mono">
                    {metadata.section_id.substring(0, 12)}...
                  </span>
                </div>
              )}
              {metadata.note_id && (
                <div>
                  <span className="text-gray-500">Note: </span>
                  <span className="text-gray-900 font-mono">
                    {metadata.note_id.substring(0, 12)}...
                  </span>
                </div>
              )}
              {event.docId && (
                <div>
                  <span className="text-gray-500">Doc: </span>
                  <span className="text-gray-900 font-mono text-xs">
                    {event.docId.substring(0, 15)}...
                  </span>
                </div>
              )}
              {event.selectionId && (
                <div>
                  <span className="text-gray-500">Selection ID: </span>
                  <span className="text-gray-900 font-mono text-xs">
                    {event.selectionId.substring(0, 12)}...
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function SectionRenderedEventCard({ event, index }: { event: RawTelemetryEvent; index: number }) {
  const metadata = event.metadata || {};

  return (
    <Card className="shadow-sm border border-gray-200">
      <CardContent className="p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className="h-6 w-6 rounded-full bg-indigo-100 flex items-center justify-center">
              <Description className="h-4 w-4 text-indigo-600" />
            </div>
            <Chip label={`#${index + 1}`} size="small" variant="outlined" className="text-xs h-5" />
            <Chip
              label={event.type || 'section_rendered'}
              size="small"
              color="primary"
              className="text-xs h-5"
            />
            {metadata.section_title && (
              <span className="text-sm font-semibold text-gray-900">{metadata.section_title}</span>
            )}
          </div>
          {event.received_at && (
            <span className="text-xs text-gray-500">{formatDateTime(event.received_at)}</span>
          )}
        </div>

        <div className="grid grid-cols-3 gap-2">
          {/* Section Title */}
          <Box className="p-2 bg-blue-50 rounded border border-blue-200 col-span-2">
            <div className="flex items-center gap-1 mb-1">
              <Description className="h-3 w-3 text-blue-600" />
              <Typography variant="caption" className="text-blue-900 font-medium text-xs">
                Section
              </Typography>
            </div>
            <Typography variant="body2" className="text-blue-600 font-bold text-sm">
              {metadata.section_title || 'Untitled'}
            </Typography>
          </Box>

          {/* Module ID */}
          <Box className="p-2 bg-gray-50 rounded border border-gray-200">
            <div className="flex items-center gap-1 mb-1">
              <Typography variant="caption" className="text-gray-600 font-medium text-xs">
                Module
              </Typography>
            </div>
            <Typography variant="body2" className="text-gray-900 font-bold text-xs">
              {metadata.module_id || 'N/A'}
            </Typography>
          </Box>
        </div>

        {/* Metadata - Compact */}
        {(metadata.section_id || metadata.note_id || event.docId) && (
          <div className="mt-2 pt-2 border-t border-gray-200">
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
              {metadata.section_id && (
                <div>
                  <span className="text-gray-500">Section ID: </span>
                  <span className="text-gray-900 font-mono">
                    {metadata.section_id.substring(0, 12)}...
                  </span>
                </div>
              )}
              {metadata.note_id && (
                <div>
                  <span className="text-gray-500">Note: </span>
                  <span className="text-gray-900 font-mono">
                    {metadata.note_id.substring(0, 12)}...
                  </span>
                </div>
              )}
              {event.docId && (
                <div>
                  <span className="text-gray-500">Doc: </span>
                  <span className="text-gray-900 font-mono text-xs">
                    {event.docId.substring(0, 15)}...
                  </span>
                </div>
              )}
              {event.docVersion && (
                <div>
                  <span className="text-gray-500">v{event.docVersion}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function SectionEditedEventCard({ event, index }: { event: RawTelemetryEvent; index: number }) {
  const metadata = event.metadata || {};
  const edit = event.edit || { charsBefore: 0, charsAfter: 0, charsDelta: 0, percentDelta: 0 };
  const timings = event.timings || {};
  const source = event.source || 'unknown';
  const isIncrease = edit.charsDelta > 0;
  const isDecrease = edit.charsDelta < 0;

  return (
    <Card className="shadow-sm border border-gray-200">
      <CardContent className="p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <div
              className={`h-6 w-6 rounded-full flex items-center justify-center ${
                isIncrease ? 'bg-orange-100' : isDecrease ? 'bg-amber-100' : 'bg-yellow-100'
              }`}
            >
              <Edit
                className={`h-4 w-4 ${
                  isIncrease ? 'text-orange-600' : isDecrease ? 'text-amber-600' : 'text-yellow-600'
                }`}
              />
            </div>
            <Chip label={`#${index + 1}`} size="small" variant="outlined" className="text-xs h-5" />
            <Chip
              label={event.type || 'section_edited'}
              size="small"
              color="primary"
              className="text-xs h-5"
            />
            {metadata.section_title && (
              <span className="text-sm font-semibold text-gray-900">{metadata.section_title}</span>
            )}
          </div>
          {event.received_at && (
            <span className="text-xs text-gray-500">{formatDateTime(event.received_at)}</span>
          )}
        </div>

        <div className="grid grid-cols-4 gap-2">
          {/* Characters Change */}
          <Box
            className={`p-2 rounded border ${
              isIncrease
                ? 'bg-green-50 border-green-200'
                : isDecrease
                  ? 'bg-red-50 border-red-200'
                  : 'bg-gray-50 border-gray-200'
            }`}
          >
            <div className="flex items-center gap-1 mb-1">
              <Typography
                variant="caption"
                className={`font-medium text-xs ${
                  isIncrease ? 'text-green-900' : isDecrease ? 'text-red-900' : 'text-gray-600'
                }`}
              >
                Change
              </Typography>
            </div>
            <Typography
              variant="body2"
              className={`font-bold ${
                isIncrease ? 'text-green-600' : isDecrease ? 'text-red-600' : 'text-gray-900'
              }`}
            >
              {edit.charsDelta > 0 ? '+' : ''}
              {edit.charsDelta?.toLocaleString() || 0}
            </Typography>
            <Typography
              variant="caption"
              className={`text-xs ${
                isIncrease ? 'text-green-700' : isDecrease ? 'text-red-700' : 'text-gray-500'
              }`}
            >
              {edit.charsBefore || 0} → {edit.charsAfter || 0}
            </Typography>
          </Box>

          {/* Percent Change */}
          <Box className="p-2 bg-blue-50 rounded border border-blue-200">
            <div className="flex items-center gap-1 mb-1">
              <Typography variant="caption" className="text-blue-900 font-medium text-xs">
                % Change
              </Typography>
            </div>
            <Typography variant="body2" className="text-blue-600 font-bold">
              {edit.percentDelta ? `${(edit.percentDelta * 100).toFixed(1)}%` : '0%'}
            </Typography>
          </Box>

          {/* Time in Edit */}
          <Box className="p-2 bg-gray-50 rounded border border-gray-200">
            <div className="flex items-center gap-1 mb-1">
              <Typography variant="caption" className="text-gray-600 font-medium text-xs">
                Edit Time
              </Typography>
            </div>
            <Typography variant="body2" className="text-gray-900 font-bold">
              {timings.msInEdit
                ? timings.msInEdit < 1000
                  ? `${Math.round(timings.msInEdit)}ms`
                  : timings.msInEdit < 60000
                    ? `${(timings.msInEdit / 1000).toFixed(1)}s`
                    : `${Math.floor(timings.msInEdit / 60000)}m ${Math.floor((timings.msInEdit % 60000) / 1000)}s`
                : 'N/A'}
            </Typography>
          </Box>

          {/* Source */}
          <Box className="p-2 bg-gray-50 rounded border border-gray-200">
            <div className="flex items-center gap-1 mb-1">
              <Typography variant="caption" className="text-gray-600 font-medium text-xs">
                Source
              </Typography>
            </div>
            <Typography variant="body2" className="text-gray-900 font-bold text-xs capitalize">
              {source === 'ai_edit'
                ? 'AI Edit'
                : source === 'manual'
                  ? 'Manual'
                  : source === 'regeneration'
                    ? 'Regeneration'
                    : source}
            </Typography>
          </Box>
        </div>

        {/* Metadata - Compact */}
        {(metadata.section_id || metadata.note_id || event.docId) && (
          <div className="mt-2 pt-2 border-t border-gray-200">
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
              {metadata.section_id && (
                <div>
                  <span className="text-gray-500">Section ID: </span>
                  <span className="text-gray-900 font-mono">
                    {metadata.section_id.substring(0, 12)}...
                  </span>
                </div>
              )}
              {metadata.note_id && (
                <div>
                  <span className="text-gray-500">Note: </span>
                  <span className="text-gray-900 font-mono">
                    {metadata.note_id.substring(0, 12)}...
                  </span>
                </div>
              )}
              {event.docId && (
                <div>
                  <span className="text-gray-500">Doc: </span>
                  <span className="text-gray-900 font-mono text-xs">
                    {event.docId.substring(0, 15)}...
                  </span>
                </div>
              )}
              {event.docVersion && (
                <div>
                  <span className="text-gray-500">v{event.docVersion}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function NoteViewedEventCard({ event, index }: { event: RawTelemetryEvent; index: number }) {
  const metadata = event.metadata || {};
  const noteId = metadata.note_id || event.docId?.replace('note:', '').split(':')[0] || '';

  return (
    <Card className="shadow-sm border border-gray-200">
      <CardContent className="p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className="h-6 w-6 rounded-full bg-cyan-100 flex items-center justify-center">
              <Visibility className="h-4 w-4 text-cyan-600" />
            </div>
            <Chip label={`#${index + 1}`} size="small" variant="outlined" className="text-xs h-5" />
            <Chip
              label={event.type || 'note_viewed'}
              size="small"
              color="primary"
              className="text-xs h-5"
            />
          </div>
          {event.received_at && (
            <span className="text-xs text-gray-500">{formatDateTime(event.received_at)}</span>
          )}
        </div>

        <div className="grid grid-cols-2 gap-2">
          {/* Note ID */}
          <Box className="p-2 bg-blue-50 rounded border border-blue-200">
            <div className="flex items-center gap-1 mb-1">
              <Description className="h-3 w-3 text-blue-600" />
              <Typography variant="caption" className="text-blue-900 font-medium text-xs">
                Note ID
              </Typography>
            </div>
            <Typography
              variant="body2"
              className="text-blue-600 font-bold text-xs font-mono break-all"
            >
              {noteId || 'N/A'}
            </Typography>
          </Box>

          {/* Document Version */}
          <Box className="p-2 bg-gray-50 rounded border border-gray-200">
            <div className="flex items-center gap-1 mb-1">
              <Typography variant="caption" className="text-gray-600 font-medium text-xs">
                Version
              </Typography>
            </div>
            <Typography variant="body2" className="text-gray-900 font-bold">
              v{event.docVersion || '0'}
            </Typography>
          </Box>
        </div>

        {/* Metadata - Compact */}
        {(metadata.encounter_id || metadata.note_id || event.docId) && (
          <div className="mt-2 pt-2 border-t border-gray-200">
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
              {metadata.encounter_id && (
                <div>
                  <span className="text-gray-500">Encounter: </span>
                  <span className="text-gray-900 font-mono">
                    {metadata.encounter_id.substring(0, 12)}...
                  </span>
                </div>
              )}
              {metadata.note_id && (
                <div>
                  <span className="text-gray-500">Note: </span>
                  <span className="text-gray-900 font-mono">
                    {metadata.note_id.substring(0, 12)}...
                  </span>
                </div>
              )}
              {event.docId && (
                <div>
                  <span className="text-gray-500">Doc: </span>
                  <span className="text-gray-900 font-mono text-xs">
                    {event.docId.substring(0, 20)}...
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function RawEventCard({ event, index }: { event: RawTelemetryEvent; index: number }) {
  // Render specific event types with custom components
  if (event.type === 'section_interacted') {
    return <SectionInteractedEventCard event={event} index={index} />;
  }
  if (event.type === 'text_copied') {
    return <TextCopiedEventCard event={event} index={index} />;
  }
  if (event.type === 'text_selected') {
    return <TextSelectedEventCard event={event} index={index} />;
  }
  if (event.type === 'section_rendered') {
    return <SectionRenderedEventCard event={event} index={index} />;
  }
  if (event.type === 'section_edited') {
    return <SectionEditedEventCard event={event} index={index} />;
  }
  if (event.type === 'note_viewed') {
    return <NoteViewedEventCard event={event} index={index} />;
  }

  // Default fallback for other event types
  return (
    <div className="border border-gray-200 rounded p-3 bg-gray-50 text-xs font-mono">
      <div className="flex items-start justify-between mb-2">
        <div className="text-gray-500 font-semibold">
          Event {index + 1}
          {event.type && <span className="ml-2 text-blue-600">{event.type}</span>}
        </div>
        {(event.timestamp || event.received_at) && (
          <span className="text-gray-400">
            {formatDateTime(event.timestamp || event.received_at)}
          </span>
        )}
      </div>
      <pre className="text-xs text-gray-700 whitespace-pre-wrap break-words overflow-x-auto">
        {JSON.stringify(event, null, 2)}
      </pre>
    </div>
  );
}

interface SectionDetailsModalProps {
  open: boolean;
  onClose: () => void;
  sectionDetails: any | null;
  rawEvents: RawTelemetryEvent[] | undefined;
  isLoading: boolean;
}

function SectionDetailsModal({
  open,
  onClose,
  sectionDetails,
  rawEvents,
  isLoading,
}: SectionDetailsModalProps) {
  if (!sectionDetails) return null;

  // Collect all event IDs from the section details
  const getAllEventIds = (section: any): string[] => {
    const eventIds: string[] = [];

    // Selection event IDs
    if (
      section.selection?.selection_event_ids &&
      Array.isArray(section.selection.selection_event_ids)
    ) {
      eventIds.push(...section.selection.selection_event_ids);
    }

    // Copy event IDs
    if (section.copy?.copy_event_ids && Array.isArray(section.copy.copy_event_ids)) {
      eventIds.push(...section.copy.copy_event_ids);
    }

    // Interaction event IDs
    if (
      section.interaction?.interaction_event_ids &&
      Array.isArray(section.interaction.interaction_event_ids)
    ) {
      eventIds.push(...section.interaction.interaction_event_ids);
    }

    // Edit event IDs
    if (section.edit?.edit_event_ids && Array.isArray(section.edit.edit_event_ids)) {
      eventIds.push(...section.edit.edit_event_ids);
    }

    return eventIds;
  };

  const eventIds = getAllEventIds(sectionDetails);

  // Filter raw events to only those matching the event IDs
  const sectionEvents = rawEvents
    ? rawEvents.filter(event => {
        const eventId = event.event_id || event.id;
        return eventId && eventIds.includes(eventId);
      })
    : [];

  // Sort events by timestamp if available
  const sortedEvents = [...sectionEvents].sort((a, b) => {
    const timeA = a.timestamp || a.received_at || '';
    const timeB = b.timestamp || b.received_at || '';
    return timeA.localeCompare(timeB);
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <Folder color="primary" />
            <Box>
              <Typography variant="h6">
                {sectionDetails.section_title || 'Untitled Section'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {sectionDetails.section_id} • {sortedEvents.length} events
              </Typography>
            </Box>
          </Box>
          <Button onClick={onClose} size="small" startIcon={<Close />}>
            Close
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress />
            <span className="ml-3 text-sm text-gray-500">Loading events...</span>
          </Box>
        ) : sortedEvents.length > 0 ? (
          <div className="space-y-3 max-h-[70vh] overflow-y-auto">
            {sortedEvents.map((event, index) => (
              <RawEventCard key={index} event={event} index={index} />
            ))}
          </div>
        ) : (
          <div className="text-sm text-gray-500 py-4 text-center">
            No events found for this section.
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function EncounterTelemetryCard({ encounterId, noteId }: EncounterTelemetryCardProps) {
  const [showRawEvents, setShowRawEvents] = useState(false);
  const [selectedSection, setSelectedSection] = useState<any | null>(null);
  const [noteUsageExpanded, setNoteUsageExpanded] = useState(false);
  const [sectionAnalyticsExpanded, setSectionAnalyticsExpanded] = useState(false);
  const [documentAnalyticsExpanded, setDocumentAnalyticsExpanded] = useState(false);

  const { data, isLoading, error } = useGetEncounterTelemetryQuery(
    { encounterId: encounterId || '' },
    { skip: !encounterId },
  );

  const { data: analyticsData, isLoading: isLoadingAnalytics } =
    useGetEncounterTelemetryAnalyticsQuery(
      { encounterId, noteId },
      { skip: !encounterId && !noteId },
    );

  // Use analytics data if available, otherwise fall back to basic telemetry data
  const hasAnalytics = analyticsData?.analytics;
  const sessionSummary = analyticsData?.analytics?.session_summary;

  // Get note_id and encounter_id from analyticsData (EncounterTelemetryAnalytics) or fall back to data (TelemetrySessionResponse)
  const note_id = analyticsData?.analytics?.note_id || data?.note_id;
  const encounter_id = analyticsData?.analytics?.encounter_id || encounterId || data?.encounter_id;

  if (isLoading) {
    return (
      <Card>
        <CardHeader
          title={
            <div className="flex items-center">
              <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                <Analytics className="h-5 w-5 text-blue-600" />
              </div>
              <span className="text-xl font-semibold text-gray-900">
                Encounter Telemetry Analytics
              </span>
            </div>
          }
          action={
            note_id ? (
              <Link
                to={`/admin-v2/note/${note_id}`}
                className="flex items-center gap-2 px-3 py-1.5 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors border border-blue-200 hover:border-blue-300"
                title={`View note ${note_id}`}
              >
                <Note className="h-4 w-4" />
                <span className="font-medium">View Note</span>
                <Launch className="h-3 w-3" />
              </Link>
            ) : null
          }
        />
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <CircularProgress size={24} />
            <span className="ml-3 text-sm text-gray-500">Loading telemetry data...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Show error only if both endpoints fail and no data is available
  if ((error || !data) && (!hasAnalytics || isLoadingAnalytics)) {
    return (
      <Card>
        <CardHeader
          title={
            <div className="flex items-center">
              <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                <Analytics className="h-5 w-5 text-blue-600" />
              </div>
              <span className="text-xl font-semibold text-gray-900">
                Encounter Telemetry Analytics
              </span>
            </div>
          }
          action={
            note_id ? (
              <Link
                to={`/admin-v2/note/${note_id}`}
                className="flex items-center gap-2 px-3 py-1.5 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors border border-blue-200 hover:border-blue-300"
                title={`View note ${note_id}`}
              >
                <Note className="h-4 w-4" />
                <span className="font-medium">View Note</span>
                <Launch className="h-3 w-3" />
              </Link>
            ) : null
          }
        />
        <CardContent>
          <p className="text-sm text-gray-500">No telemetry data available for this encounter.</p>
        </CardContent>
      </Card>
    );
  }

  const { created_at, updated_at } = data || {};

  return (
    <Card>
      <CardHeader
        title={
          <div className="flex items-center">
            <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center mr-3">
              <Analytics className="h-5 w-5 text-blue-600" />
            </div>
            <span className="text-xl font-semibold text-gray-900">
              Encounter Telemetry Analytics
            </span>
          </div>
        }
        action={
          noteId && encounter_id ? (
            <Link
              to={`/admin-v2/encounter/${encounter_id}`}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-purple-600 hover:text-purple-800 hover:bg-purple-50 rounded-lg transition-colors border border-purple-200 hover:border-purple-300"
              title={`View encounter ${encounter_id}`}
            >
              <Description className="h-4 w-4" />
              <span className="font-medium">View Encounter</span>
              <Launch className="h-3 w-3" />
            </Link>
          ) : note_id && !noteId ? (
            <Link
              to={`/admin-v2/note/${note_id}`}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors border border-blue-200 hover:border-blue-300"
              title={`View note ${note_id}`}
            >
              <Note className="h-4 w-4" />
              <span className="font-medium">View Note</span>
              <Launch className="h-3 w-3" />
            </Link>
          ) : null
        }
      />
      <CardContent>
        <div className="space-y-4">
          {/* Summary Cards Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {/* Total Events */}
            <Box className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <Typography variant="caption" className="text-blue-900 font-medium text-xs">
                Total Events
              </Typography>
              <Typography variant="h4" className="text-blue-600 font-bold mt-1">
                {sessionSummary?.total_events?.toLocaleString() || (data ? 'N/A' : '0')}
              </Typography>
            </Box>

            {/* Session Duration */}
            {sessionSummary &&
              sessionSummary.session_duration_seconds !== null &&
              sessionSummary.session_duration_seconds !== undefined && (
                <Box className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <Typography variant="caption" className="text-purple-900 font-medium text-xs">
                    Duration
                  </Typography>
                  <Typography variant="h6" className="text-purple-600 font-bold mt-1">
                    {Math.floor(sessionSummary.session_duration_seconds / 60)}m{' '}
                    {Math.floor(sessionSummary.session_duration_seconds % 60)}s
                  </Typography>
                </Box>
              )}

            {/* Events per Minute */}
            {sessionSummary &&
              sessionSummary.event_frequency_per_minute !== null &&
              sessionSummary.event_frequency_per_minute !== undefined && (
                <Box className="p-4 bg-indigo-50 rounded-lg border border-indigo-200">
                  <Typography variant="caption" className="text-indigo-900 font-medium text-xs">
                    Events/Min
                  </Typography>
                  <Typography variant="h6" className="text-indigo-600 font-bold mt-1">
                    {sessionSummary.event_frequency_per_minute.toFixed(2)}
                  </Typography>
                </Box>
              )}

            {/* Last Updated */}
            {updated_at && (
              <Box className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <Typography variant="caption" className="text-gray-900 font-medium text-xs">
                  Last Updated
                </Typography>
                <Typography variant="body2" className="text-gray-700 font-semibold mt-1 text-xs">
                  {formatDateTime(updated_at)}
                </Typography>
              </Box>
            )}
          </div>

          {/* Document Analytics Comparison */}
          {analyticsData?.analytics?.document_analytics &&
            analyticsData.analytics.document_analytics.length > 0 && (
              <div className="pt-4 border-t border-gray-200">
                <div
                  className="text-sm font-medium text-gray-500 mb-3 flex items-center cursor-pointer hover:text-gray-700 transition-colors"
                  onClick={() => setDocumentAnalyticsExpanded(!documentAnalyticsExpanded)}
                >
                  <div className="h-5 w-5 rounded-full bg-indigo-100 flex items-center justify-center mr-3">
                    <Description className="h-3 w-3 text-indigo-600" />
                  </div>
                  <span className="flex-1">Document Analytics Comparison</span>
                  <IconButton
                    size="small"
                    onClick={e => {
                      e.stopPropagation();
                      setDocumentAnalyticsExpanded(!documentAnalyticsExpanded);
                    }}
                    className="ml-2"
                  >
                    <ExpandMore
                      sx={{
                        transform: documentAnalyticsExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s',
                      }}
                    />
                  </IconButton>
                </div>

                {/* Summary - Always Visible When Collapsed */}
                {!documentAnalyticsExpanded && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-2">
                    <Box className="p-3 bg-gray-50 rounded border border-gray-200">
                      <Typography variant="caption" className="text-gray-900 font-medium text-xs">
                        Total Documents
                      </Typography>
                      <Typography variant="h6" className="text-gray-900 font-bold mt-1">
                        {analyticsData.analytics.document_analytics.length}
                      </Typography>
                    </Box>
                    <Box className="p-3 bg-blue-50 rounded border border-blue-200">
                      <Typography variant="caption" className="text-blue-900 font-medium text-xs">
                        Total Engagement
                      </Typography>
                      <Typography variant="h6" className="text-blue-600 font-bold mt-1">
                        {analyticsData.analytics.document_analytics
                          .reduce((sum: number, d: any) => sum + (d.engagement_score ?? 0), 0)
                          .toLocaleString()}
                      </Typography>
                    </Box>
                    <Box className="p-3 bg-indigo-50 rounded border border-indigo-200">
                      <Typography variant="caption" className="text-indigo-900 font-medium text-xs">
                        Total Views
                      </Typography>
                      <Typography variant="h6" className="text-indigo-600 font-bold mt-1">
                        {analyticsData.analytics.document_analytics
                          .reduce((sum: number, d: any) => sum + (d.view_count ?? 0), 0)
                          .toLocaleString()}
                      </Typography>
                    </Box>
                    <Box className="p-3 bg-green-50 rounded border border-green-200">
                      <Typography variant="caption" className="text-green-900 font-medium text-xs">
                        Total Copies
                      </Typography>
                      <Typography variant="h6" className="text-green-600 font-bold mt-1">
                        {analyticsData.analytics.document_analytics
                          .reduce((sum: number, d: any) => sum + (d.copy_count ?? 0), 0)
                          .toLocaleString()}
                      </Typography>
                    </Box>
                  </div>
                )}

                {isLoadingAnalytics ? (
                  <div className="flex items-center justify-center py-4">
                    <CircularProgress size={20} />
                    <span className="ml-2 text-sm text-gray-500">Loading analytics...</span>
                  </div>
                ) : (
                  <Collapse in={documentAnalyticsExpanded}>
                    <div style={{ height: 400, width: '100%' }}>
                      <DataGrid
                        rows={analyticsData.analytics.document_analytics.map(
                          (doc: any, index: number) => ({
                            id: doc.doc_id || `doc-${index}`,
                            doc_id: doc.doc_id,
                            doc_type: doc.doc_type,
                            view_count: doc.view_count ?? 0,
                            select_count: doc.select_count ?? 0,
                            copy_count: doc.copy_count ?? 0,
                            click_count: doc.click_count ?? 0,
                            edit_count: doc.edit_count ?? 0,
                            engagement: doc.engagement_score ?? 0,
                          }),
                        )}
                        columns={[
                          {
                            field: 'doc_id',
                            headerName: 'Document',
                            flex: 2,
                            minWidth: 250,
                            renderCell: params => (
                              <div className="font-mono text-xs text-gray-900 break-all">
                                {params.value.length > 40
                                  ? `${params.value.substring(0, 40)}...`
                                  : params.value}
                              </div>
                            ),
                          },
                          {
                            field: 'engagement',
                            headerName: 'Engagement',
                            type: 'number',
                            width: 130,
                            align: 'right',
                            headerAlign: 'right',
                            renderCell: params => (
                              <span className="font-bold text-right text-blue-600">
                                {params.value.toLocaleString()}
                              </span>
                            ),
                          },
                          {
                            field: 'doc_type',
                            headerName: 'Type',
                            width: 120,
                            renderCell: params => (
                              <Chip
                                label={params.value}
                                size="small"
                                variant="outlined"
                                color={params.value === 'note' ? 'primary' : 'default'}
                              />
                            ),
                          },
                          {
                            field: 'view_count',
                            headerName: 'Views',
                            type: 'number',
                            width: 110,
                            align: 'right',
                            headerAlign: 'right',
                          },
                          {
                            field: 'select_count',
                            headerName: 'Selections',
                            type: 'number',
                            width: 110,
                            align: 'right',
                            headerAlign: 'right',
                          },
                          {
                            field: 'copy_count',
                            headerName: 'Copies',
                            type: 'number',
                            width: 110,
                            align: 'right',
                            headerAlign: 'right',
                          },
                          {
                            field: 'click_count',
                            headerName: 'Clicks',
                            type: 'number',
                            width: 110,
                            align: 'right',
                            headerAlign: 'right',
                          },
                          {
                            field: 'edit_count',
                            headerName: 'Edits',
                            type: 'number',
                            width: 110,
                            align: 'right',
                            headerAlign: 'right',
                          },
                        ]}
                        disableRowSelectionOnClick
                        pageSizeOptions={[10, 25, 50, 100]}
                        initialState={{
                          pagination: {
                            paginationModel: { pageSize: 25 },
                          },
                          sorting: {
                            sortModel: [{ field: 'engagement', sort: 'desc' }],
                          },
                        }}
                        sx={{
                          '& .MuiDataGrid-row': {
                            borderBottom: '1px solid',
                            borderBottomColor: 'grey.200',
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                              backgroundColor: 'action.hover',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                              transform: 'translateY(-1px)',
                            },
                            '&:last-child': {
                              borderBottom: 'none',
                            },
                          },
                          '& .MuiDataGrid-cell': {
                            padding: '12px 16px',
                          },
                          '& .MuiDataGrid-columnHeader': {
                            backgroundColor: 'grey.50',
                            fontWeight: 600,
                            borderBottom: '2px solid',
                            borderBottomColor: 'divider',
                          },
                          '& .MuiDataGrid-columnHeaderTitle': {
                            fontWeight: 600,
                          },
                        }}
                      />
                    </div>
                  </Collapse>
                )}
                {analyticsData.analytics.document_analytics.length > 1 && (
                  <div className="mt-3 text-xs text-gray-500">
                    Engagement Score = Views/Rendered × 1 + Copies × 10 + Edits × 20 + Hover
                    Duration × 0.1/sec
                  </div>
                )}
              </div>
            )}

          {/* Note Usage Analytics */}
          {analyticsData?.analytics?.note_usage_sections &&
            analyticsData.analytics.note_usage_sections.length > 0 && (
              <div className="pt-4 border-t border-gray-200">
                <div
                  className="text-sm font-medium text-gray-500 mb-3 flex items-center cursor-pointer hover:text-gray-700 transition-colors"
                  onClick={() => setNoteUsageExpanded(!noteUsageExpanded)}
                >
                  <div className="h-5 w-5 rounded-full bg-emerald-100 flex items-center justify-center mr-3">
                    <Note className="h-3 w-3 text-emerald-600" />
                  </div>
                  <span className="flex-1">Note Usage Analytics</span>
                  <IconButton
                    size="small"
                    onClick={e => {
                      e.stopPropagation();
                      setNoteUsageExpanded(!noteUsageExpanded);
                    }}
                    className="ml-2"
                  >
                    <ExpandMore
                      sx={{
                        transform: noteUsageExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s',
                      }}
                    />
                  </IconButton>
                </div>
                {isLoadingAnalytics ? (
                  <div className="flex items-center justify-center py-4">
                    <CircularProgress size="20" />
                    <span className="ml-2 text-sm text-gray-500">Loading note usage...</span>
                  </div>
                ) : null}

                {/* Summary Cards - Always Visible When Collapsed */}
                {!noteUsageExpanded && analyticsData.analytics.note_usage && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-2">
                    <Box className="p-3 bg-blue-50 rounded border border-blue-200">
                      <Typography variant="caption" className="text-blue-900 font-medium text-xs">
                        Sections Rendered
                      </Typography>
                      <Typography variant="h6" className="text-blue-600 font-bold mt-1">
                        {analyticsData.analytics.note_usage.percent_rendered !== null &&
                        analyticsData.analytics.note_usage.percent_rendered !== undefined
                          ? `${(analyticsData.analytics.note_usage.percent_rendered * 100).toFixed(1)}%`
                          : 'N/A'}
                      </Typography>
                    </Box>
                    <Box className="p-3 bg-purple-50 rounded border border-purple-200">
                      <Typography variant="caption" className="text-purple-900 font-medium text-xs">
                        Sections Interacted
                      </Typography>
                      <Typography variant="h6" className="text-purple-600 font-bold mt-1">
                        {analyticsData.analytics.note_usage.percent_interacted !== null &&
                        analyticsData.analytics.note_usage.percent_interacted !== undefined
                          ? `${(analyticsData.analytics.note_usage.percent_interacted * 100).toFixed(1)}%`
                          : 'N/A'}
                      </Typography>
                    </Box>
                    <Box className="p-3 bg-green-50 rounded border border-green-200">
                      <Typography variant="caption" className="text-green-900 font-medium text-xs">
                        Sections Copied
                      </Typography>
                      <Typography variant="h6" className="text-green-600 font-bold mt-1">
                        {analyticsData.analytics.note_usage.percent_copied !== null &&
                        analyticsData.analytics.note_usage.percent_copied !== undefined
                          ? `${(analyticsData.analytics.note_usage.percent_copied * 100).toFixed(1)}%`
                          : 'N/A'}
                      </Typography>
                    </Box>
                    <Box className="p-3 bg-orange-50 rounded border border-orange-200">
                      <Typography variant="caption" className="text-orange-900 font-medium text-xs">
                        Sections Edited
                      </Typography>
                      <Typography variant="h6" className="text-orange-600 font-bold mt-1">
                        {analyticsData.analytics.note_usage.percent_edited !== null &&
                        analyticsData.analytics.note_usage.percent_edited !== undefined
                          ? `${(analyticsData.analytics.note_usage.percent_edited * 100).toFixed(1)}%`
                          : 'N/A'}
                      </Typography>
                    </Box>
                  </div>
                )}

                {isLoadingAnalytics ? (
                  <div className="flex items-center justify-center py-4">
                    <CircularProgress size={20} />
                    <span className="ml-2 text-sm text-gray-500">Loading note usage...</span>
                  </div>
                ) : (
                  <Collapse in={noteUsageExpanded}>
                    <div className="space-y-4">
                      {/* Summary Cards - Full View */}
                      {analyticsData.analytics.note_usage && (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                          <Box className="p-3 bg-blue-50 rounded border border-blue-200">
                            <Typography
                              variant="caption"
                              className="text-blue-900 font-medium text-xs"
                            >
                              Sections Rendered
                            </Typography>
                            <Typography variant="h6" className="text-blue-600 font-bold mt-1">
                              {analyticsData.analytics.note_usage.percent_rendered !== null &&
                              analyticsData.analytics.note_usage.percent_rendered !== undefined
                                ? `${(analyticsData.analytics.note_usage.percent_rendered * 100).toFixed(1)}%`
                                : 'N/A'}
                            </Typography>
                          </Box>
                          <Box className="p-3 bg-purple-50 rounded border border-purple-200">
                            <Typography
                              variant="caption"
                              className="text-purple-900 font-medium text-xs"
                            >
                              Sections Interacted
                            </Typography>
                            <Typography variant="h6" className="text-purple-600 font-bold mt-1">
                              {analyticsData.analytics.note_usage.percent_interacted !== null &&
                              analyticsData.analytics.note_usage.percent_interacted !== undefined
                                ? `${(analyticsData.analytics.note_usage.percent_interacted * 100).toFixed(1)}%`
                                : 'N/A'}
                            </Typography>
                          </Box>
                          <Box className="p-3 bg-green-50 rounded border border-green-200">
                            <Typography
                              variant="caption"
                              className="text-green-900 font-medium text-xs"
                            >
                              Sections Copied
                            </Typography>
                            <Typography variant="h6" className="text-green-600 font-bold mt-1">
                              {analyticsData.analytics.note_usage.percent_copied !== null &&
                              analyticsData.analytics.note_usage.percent_copied !== undefined
                                ? `${(analyticsData.analytics.note_usage.percent_copied * 100).toFixed(1)}%`
                                : 'N/A'}
                            </Typography>
                          </Box>
                          <Box className="p-3 bg-orange-50 rounded border border-orange-200">
                            <Typography
                              variant="caption"
                              className="text-orange-900 font-medium text-xs"
                            >
                              Sections Edited
                            </Typography>
                            <Typography variant="h6" className="text-orange-600 font-bold mt-1">
                              {analyticsData.analytics.note_usage.percent_edited !== null &&
                              analyticsData.analytics.note_usage.percent_edited !== undefined
                                ? `${(analyticsData.analytics.note_usage.percent_edited * 100).toFixed(1)}%`
                                : 'N/A'}
                            </Typography>
                          </Box>
                        </div>
                      )}

                      {/* Char-Weighted Coverage Metrics */}
                      {analyticsData.analytics.note_usage &&
                        (analyticsData.analytics.note_usage.char_weighted_selection_percent_all !==
                          null ||
                          analyticsData.analytics.note_usage
                            .char_weighted_selection_percent_known !== null ||
                          analyticsData.analytics.note_usage.char_weighted_copy_percent_all !==
                            null ||
                          analyticsData.analytics.note_usage.char_weighted_copy_percent_known !==
                            null ||
                          analyticsData.analytics.note_usage.char_weighted_edit_percent_all !==
                            null ||
                          analyticsData.analytics.note_usage.char_weighted_edit_percent_known !==
                            null) && (
                          <div className="mb-4">
                            <Typography
                              variant="caption"
                              className="text-gray-700 font-medium text-xs mb-2 block"
                            >
                              Char-Weighted Coverage Metrics
                            </Typography>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                              {analyticsData.analytics.note_usage
                                .char_weighted_selection_percent_all !== null && (
                                <Box className="p-2 bg-indigo-50 rounded border border-indigo-200">
                                  <Typography
                                    variant="caption"
                                    className="text-indigo-900 font-medium text-xs"
                                  >
                                    Selection Coverage (All)
                                  </Typography>
                                  <Typography variant="body2" className="text-indigo-600 font-bold">
                                    {analyticsData.analytics.note_usage
                                      .char_weighted_selection_percent_all !== null &&
                                    analyticsData.analytics.note_usage
                                      .char_weighted_selection_percent_all !== undefined
                                      ? `${(
                                          analyticsData.analytics.note_usage
                                            .char_weighted_selection_percent_all * 100
                                        ).toFixed(1)}%`
                                      : 'N/A'}
                                  </Typography>
                                  {analyticsData.analytics.note_usage
                                    .char_weighted_selection_percent_known !== null &&
                                    analyticsData.analytics.note_usage
                                      .char_weighted_selection_percent_known !== undefined && (
                                      <Typography
                                        variant="caption"
                                        className="text-indigo-700 text-xs"
                                      >
                                        Known:{' '}
                                        {(
                                          analyticsData.analytics.note_usage
                                            .char_weighted_selection_percent_known * 100
                                        ).toFixed(1)}
                                        %
                                      </Typography>
                                    )}
                                </Box>
                              )}
                              {analyticsData.analytics.note_usage.char_weighted_copy_percent_all !==
                                null && (
                                <Box className="p-2 bg-teal-50 rounded border border-teal-200">
                                  <Typography
                                    variant="caption"
                                    className="text-teal-900 font-medium text-xs"
                                  >
                                    Copy Coverage (All)
                                  </Typography>
                                  <Typography variant="body2" className="text-teal-600 font-bold">
                                    {analyticsData.analytics.note_usage
                                      .char_weighted_copy_percent_all !== null &&
                                    analyticsData.analytics.note_usage
                                      .char_weighted_copy_percent_all !== undefined
                                      ? `${(
                                          analyticsData.analytics.note_usage
                                            .char_weighted_copy_percent_all * 100
                                        ).toFixed(1)}%`
                                      : 'N/A'}
                                  </Typography>
                                  {analyticsData.analytics.note_usage
                                    .char_weighted_copy_percent_known !== null &&
                                    analyticsData.analytics.note_usage
                                      .char_weighted_copy_percent_known !== undefined && (
                                      <Typography
                                        variant="caption"
                                        className="text-teal-700 text-xs"
                                      >
                                        Known:{' '}
                                        {(
                                          analyticsData.analytics.note_usage
                                            .char_weighted_copy_percent_known * 100
                                        ).toFixed(1)}
                                        %
                                      </Typography>
                                    )}
                                </Box>
                              )}
                              {analyticsData.analytics.note_usage.char_weighted_edit_percent_all !==
                                null && (
                                <Box className="p-2 bg-amber-50 rounded border border-amber-200">
                                  <Typography
                                    variant="caption"
                                    className="text-amber-900 font-medium text-xs"
                                  >
                                    Edit Coverage (All)
                                  </Typography>
                                  <Typography variant="body2" className="text-amber-600 font-bold">
                                    {analyticsData.analytics.note_usage
                                      .char_weighted_edit_percent_all !== null &&
                                    analyticsData.analytics.note_usage
                                      .char_weighted_edit_percent_all !== undefined
                                      ? `${(
                                          analyticsData.analytics.note_usage
                                            .char_weighted_edit_percent_all * 100
                                        ).toFixed(1)}%`
                                      : 'N/A'}
                                  </Typography>
                                  {analyticsData.analytics.note_usage
                                    .char_weighted_edit_percent_known !== null &&
                                    analyticsData.analytics.note_usage
                                      .char_weighted_edit_percent_known !== undefined && (
                                      <Typography
                                        variant="caption"
                                        className="text-amber-700 text-xs"
                                      >
                                        Known:{' '}
                                        {(
                                          analyticsData.analytics.note_usage
                                            .char_weighted_edit_percent_known * 100
                                        ).toFixed(1)}
                                        %
                                      </Typography>
                                    )}
                                </Box>
                              )}
                            </div>
                          </div>
                        )}

                      {/* Per-Section Breakdown Table */}
                      <div style={{ height: 400, width: '100%' }}>
                        <DataGrid
                          rows={analyticsData.analytics.note_usage_sections.map(
                            (section: any, index: number) => {
                              const { title, displayId } = cleanSectionDisplay(section);
                              return {
                                id: section.section_id || `section-${index}`,
                                section_title: title,
                                section_id: section.section_id,
                                displayId,
                                section_chars: section.section_chars ?? 0,
                                rendered: section.rendered ?? false,
                                interacted: section.interacted ?? false,
                                copied: section.copied ?? false,
                                edited: section.edited ?? false,
                                selection_percent_max: section.selection_percent_max,
                                copy_percent_max: section.copy_percent_max,
                                edited_percent: section.edited_percent,
                              };
                            },
                          )}
                          columns={[
                            {
                              field: 'section_title',
                              headerName: 'Section',
                              flex: 2,
                              minWidth: 200,
                              renderCell: params => (
                                <div>
                                  <div className="text-sm text-gray-900 font-medium">
                                    {params.value || 'Untitled Section'}
                                  </div>
                                  <div className="text-xs text-gray-500 font-mono mt-1">
                                    {params.row.displayId && params.row.displayId.length > 20
                                      ? `${params.row.displayId.substring(0, 20)}...`
                                      : params.row.displayId || params.row.section_id}
                                  </div>
                                </div>
                              ),
                            },
                            {
                              field: 'section_chars',
                              headerName: 'Chars',
                              type: 'number',
                              width: 100,
                              align: 'right',
                              headerAlign: 'right',
                              renderCell: params => (
                                <span className="text-gray-600">
                                  {params.value ? params.value.toLocaleString() : 'N/A'}
                                </span>
                              ),
                            },
                            {
                              field: 'rendered',
                              headerName: 'Rendered',
                              width: 100,
                              align: 'center',
                              headerAlign: 'center',
                              renderCell: params => (
                                <Chip
                                  label={params.value ? 'Yes' : 'No'}
                                  size="small"
                                  color={params.value ? 'success' : 'default'}
                                  variant={params.value ? 'filled' : 'outlined'}
                                />
                              ),
                            },
                            {
                              field: 'interacted',
                              headerName: 'Interacted',
                              width: 110,
                              align: 'center',
                              headerAlign: 'center',
                              renderCell: params => (
                                <Chip
                                  label={params.value ? 'Yes' : 'No'}
                                  size="small"
                                  color={params.value ? 'primary' : 'default'}
                                  variant={params.value ? 'filled' : 'outlined'}
                                />
                              ),
                            },
                            {
                              field: 'copied',
                              headerName: 'Copied',
                              width: 100,
                              align: 'center',
                              headerAlign: 'center',
                              renderCell: params => (
                                <Chip
                                  label={params.value ? 'Yes' : 'No'}
                                  size="small"
                                  color={params.value ? 'success' : 'default'}
                                  variant={params.value ? 'filled' : 'outlined'}
                                />
                              ),
                            },
                            {
                              field: 'edited',
                              headerName: 'Edited',
                              width: 100,
                              align: 'center',
                              headerAlign: 'center',
                              renderCell: params => (
                                <Chip
                                  label={params.value ? 'Yes' : 'No'}
                                  size="small"
                                  color={params.value ? 'warning' : 'default'}
                                  variant={params.value ? 'filled' : 'outlined'}
                                />
                              ),
                            },
                            {
                              field: 'selection_percent_max',
                              headerName: 'Max Selection %',
                              width: 130,
                              align: 'right',
                              headerAlign: 'right',
                              renderCell: params => (
                                <span className="text-purple-600 font-medium">
                                  {params.value !== null && params.value !== undefined
                                    ? `${(params.value * 100).toFixed(1)}%`
                                    : 'N/A'}
                                </span>
                              ),
                            },
                            {
                              field: 'copy_percent_max',
                              headerName: 'Max Copy %',
                              width: 120,
                              align: 'right',
                              headerAlign: 'right',
                              renderCell: params => (
                                <span className="text-green-600 font-medium">
                                  {params.value !== null && params.value !== undefined
                                    ? `${(params.value * 100).toFixed(1)}%`
                                    : 'N/A'}
                                </span>
                              ),
                            },
                            {
                              field: 'edited_percent',
                              headerName: 'Edit %',
                              width: 110,
                              align: 'right',
                              headerAlign: 'right',
                              renderCell: params => (
                                <span className="text-orange-600 font-medium">
                                  {params.value !== null && params.value !== undefined
                                    ? `${(params.value * 100).toFixed(1)}%`
                                    : 'N/A'}
                                </span>
                              ),
                            },
                          ]}
                          disableRowSelectionOnClick
                          pageSizeOptions={[10, 25, 50, 100]}
                          initialState={{
                            pagination: {
                              paginationModel: { pageSize: 25 },
                            },
                            sorting: {
                              sortModel: [{ field: 'section_title', sort: 'asc' }],
                            },
                          }}
                          sx={{
                            '& .MuiDataGrid-row': {
                              borderBottom: '1px solid',
                              borderBottomColor: 'grey.200',
                              transition: 'all 0.2s ease-in-out',
                              '&:hover': {
                                backgroundColor: 'action.hover',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                                transform: 'translateY(-1px)',
                              },
                              '&:last-child': {
                                borderBottom: 'none',
                              },
                            },
                            '& .MuiDataGrid-cell': {
                              padding: '12px 16px',
                            },
                            '& .MuiDataGrid-columnHeader': {
                              backgroundColor: 'grey.50',
                              fontWeight: 600,
                              borderBottom: '2px solid',
                              borderBottomColor: 'divider',
                            },
                            '& .MuiDataGrid-columnHeaderTitle': {
                              fontWeight: 600,
                            },
                          }}
                        />
                      </div>
                    </div>
                  </Collapse>
                )}
              </div>
            )}

          {/* Section Analytics Details */}
          {analyticsData?.analytics?.section_details &&
            analyticsData.analytics.section_details.length > 0 && (
              <div className="pt-4 border-t border-gray-200">
                <div
                  className="text-sm font-medium text-gray-500 mb-3 flex items-center cursor-pointer hover:text-gray-700 transition-colors"
                  onClick={() => setSectionAnalyticsExpanded(!sectionAnalyticsExpanded)}
                >
                  <div className="h-5 w-5 rounded-full bg-purple-100 flex items-center justify-center mr-3">
                    <Folder className="h-3 w-3 text-purple-600" />
                  </div>
                  <span className="flex-1">Section Analytics Details</span>
                  <IconButton
                    size="small"
                    onClick={e => {
                      e.stopPropagation();
                      setSectionAnalyticsExpanded(!sectionAnalyticsExpanded);
                    }}
                    className="ml-2"
                  >
                    <ExpandMore
                      sx={{
                        transform: sectionAnalyticsExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s',
                      }}
                    />
                  </IconButton>
                </div>

                {/* Summary - Always Visible When Collapsed */}
                {!sectionAnalyticsExpanded && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-2">
                    <Box className="p-3 bg-gray-50 rounded border border-gray-200">
                      <Typography variant="caption" className="text-gray-900 font-medium text-xs">
                        Total Sections
                      </Typography>
                      <Typography variant="h6" className="text-gray-900 font-bold mt-1">
                        {analyticsData.analytics.section_details.length}
                      </Typography>
                    </Box>
                    <Box className="p-3 bg-blue-50 rounded border border-blue-200">
                      <Typography variant="caption" className="text-blue-900 font-medium text-xs">
                        High Engagement
                      </Typography>
                      <Typography variant="h6" className="text-blue-600 font-bold mt-1">
                        {
                          analyticsData.analytics.section_details.filter(
                            (s: any) => (s.engagement_score ?? 0) > 10,
                          ).length
                        }
                      </Typography>
                      <Typography variant="caption" className="text-blue-700 text-xs mt-1">
                        (engagement &gt; 10)
                      </Typography>
                    </Box>
                    <Box className="p-3 bg-green-50 rounded border border-green-200">
                      <Typography variant="caption" className="text-green-900 font-medium text-xs">
                        Total Copies
                      </Typography>
                      <Typography variant="h6" className="text-green-600 font-bold mt-1">
                        {analyticsData.analytics.section_details
                          .reduce((sum: number, s: any) => sum + (s.copy?.total_copies ?? 0), 0)
                          .toLocaleString()}
                      </Typography>
                    </Box>
                    <Box className="p-3 bg-orange-50 rounded border border-orange-200">
                      <Typography variant="caption" className="text-orange-900 font-medium text-xs">
                        Total Edits
                      </Typography>
                      <Typography variant="h6" className="text-orange-600 font-bold mt-1">
                        {analyticsData.analytics.section_details
                          .reduce((sum: number, s: any) => sum + (s.edit?.total_edits ?? 0), 0)
                          .toLocaleString()}
                      </Typography>
                    </Box>
                  </div>
                )}

                {isLoadingAnalytics ? (
                  <div className="flex items-center justify-center py-4">
                    <CircularProgress size={20} />
                    <span className="ml-2 text-sm text-gray-500">Loading analytics...</span>
                  </div>
                ) : (
                  <Collapse in={sectionAnalyticsExpanded}>
                    <div style={{ height: 600, width: '100%' }}>
                      <DataGrid
                        rows={analyticsData.analytics.section_details.map(
                          (section: any, index: number) => {
                            const { title, displayId } = cleanSectionDisplay(section);
                            return {
                              id: section.section_id || `section-${index}`,
                              section_title: title,
                              section_id: section.section_id,
                              displayId,
                              doc_type: section.doc_type,
                              doc_id: section.doc_id,
                              render_count: section.render_count ?? 0,
                              selections: section.selection?.total_selections ?? 0,
                              copies: section.copy?.total_copies ?? 0,
                              clicks: section.interaction?.total_clicks ?? 0,
                              interactions: section.interaction?.total_interactions ?? 0,
                              edits: section.edit?.total_edits ?? 0,
                              hover_sec: section.interaction?.total_hover_ms
                                ? (section.interaction.total_hover_ms / 1000).toFixed(1)
                                : '0',
                              engagement: section.engagement_score ?? 0,
                              _sectionData: section, // Store full section data for modal
                            };
                          },
                        )}
                        columns={[
                          {
                            field: 'section_title',
                            headerName: 'Section',
                            flex: 2,
                            minWidth: 250,
                            renderCell: params => (
                              <div>
                                <div className="text-sm text-gray-900 font-medium">
                                  {params.row.section_title || 'Untitled Section'}
                                </div>
                                <div className="text-xs text-gray-500 font-mono mt-1">
                                  {params.row.displayId && params.row.displayId.length > 20
                                    ? `${params.row.displayId.substring(0, 20)}...`
                                    : params.row.displayId || params.row.section_id}
                                </div>
                              </div>
                            ),
                          },
                          {
                            field: 'engagement',
                            headerName: 'Engagement',
                            type: 'number',
                            width: 130,
                            align: 'right',
                            headerAlign: 'right',
                            renderCell: params => (
                              <span className="font-bold text-right text-blue-600">
                                {params.value.toLocaleString()}
                              </span>
                            ),
                          },
                          {
                            field: 'doc_type',
                            headerName: 'Document',
                            flex: 1,
                            minWidth: 150,
                            renderCell: params => (
                              <div className="flex items-center gap-2">
                                <Chip
                                  label={params.row.doc_type}
                                  size="small"
                                  variant="outlined"
                                  color={params.row.doc_type === 'note' ? 'primary' : 'default'}
                                />
                                <span className="text-xs text-gray-500 font-mono">
                                  {params.row.doc_id.length > 15
                                    ? `${params.row.doc_id.substring(0, 15)}...`
                                    : params.row.doc_id}
                                </span>
                              </div>
                            ),
                          },
                          {
                            field: 'render_count',
                            headerName: 'Rendered',
                            type: 'number',
                            width: 110,
                            align: 'right',
                            headerAlign: 'right',
                          },
                          {
                            field: 'selections',
                            headerName: 'Selections',
                            type: 'number',
                            width: 110,
                            align: 'right',
                            headerAlign: 'right',
                          },
                          {
                            field: 'copies',
                            headerName: 'Copies',
                            type: 'number',
                            width: 110,
                            align: 'right',
                            headerAlign: 'right',
                          },
                          {
                            field: 'clicks',
                            headerName: 'Clicks',
                            type: 'number',
                            width: 110,
                            align: 'right',
                            headerAlign: 'right',
                          },
                          {
                            field: 'interactions',
                            headerName: 'Interactions',
                            type: 'number',
                            width: 110,
                            align: 'right',
                            headerAlign: 'right',
                          },
                          {
                            field: 'edits',
                            headerName: 'Edits',
                            type: 'number',
                            width: 110,
                            align: 'right',
                            headerAlign: 'right',
                          },
                          {
                            field: 'hover_sec',
                            headerName: 'Hover (sec)',
                            width: 110,
                            align: 'right',
                            headerAlign: 'right',
                          },
                        ]}
                        onRowClick={(params: GridRowParams) => {
                          setSelectedSection(params.row._sectionData);
                        }}
                        disableRowSelectionOnClick
                        pageSizeOptions={[10, 25, 50, 100]}
                        initialState={{
                          pagination: {
                            paginationModel: { pageSize: 25 },
                          },
                          sorting: {
                            sortModel: [{ field: 'engagement', sort: 'desc' }],
                          },
                        }}
                        sx={{
                          '& .MuiDataGrid-cell:hover': {
                            cursor: 'pointer',
                          },
                          '& .MuiDataGrid-row': {
                            borderBottom: '1px solid',
                            borderBottomColor: 'grey.200',
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                              backgroundColor: 'action.hover',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                              transform: 'translateY(-1px)',
                            },
                            '&:last-child': {
                              borderBottom: 'none',
                            },
                          },
                          '& .MuiDataGrid-cell': {
                            padding: '12px 16px',
                          },
                          '& .MuiDataGrid-columnHeader': {
                            backgroundColor: 'grey.50',
                            fontWeight: 600,
                            borderBottom: '2px solid',
                            borderBottomColor: 'divider',
                          },
                          '& .MuiDataGrid-columnHeaderTitle': {
                            fontWeight: 600,
                          },
                        }}
                      />
                    </div>
                  </Collapse>
                )}
                {analyticsData.analytics.section_details.length > 0 && (
                  <div className="mt-3 text-xs text-gray-500">
                    Engagement Score = Views/Rendered × 1 + Copies × 10 + Edits × 20 + Hover
                    Duration × 0.1/sec
                  </div>
                )}
              </div>
            )}

          {/* Raw Events (Collapsible) */}
          {analyticsData && (
            <div className="pt-4 border-t border-gray-200">
              <div
                className="text-sm font-medium text-gray-500 mb-3 flex items-center cursor-pointer hover:text-gray-700 transition-colors"
                onClick={() => setShowRawEvents(!showRawEvents)}
              >
                <div className="h-5 w-5 rounded-full bg-gray-100 flex items-center justify-center mr-3">
                  <Analytics className="h-3 w-3 text-gray-600" />
                </div>
                <span className="flex-1 flex items-center gap-2 flex-wrap">
                  Raw Events
                  {analyticsData.analytics.raw_events && (
                    <span className="text-xs font-normal text-gray-500">
                      ({analyticsData.analytics.raw_events.length} events)
                    </span>
                  )}
                  {sessionSummary?.events_by_type &&
                    Object.keys(sessionSummary.events_by_type).length > 0 && (
                      <div className="flex flex-wrap gap-1 ml-2">
                        {Object.entries(sessionSummary.events_by_type).map(([eventType, count]) => (
                          <Chip
                            key={eventType}
                            label={`${eventType.replace(/_/g, ' ')}: ${count}`}
                            size="small"
                            variant="outlined"
                            className="text-xs h-5 text-gray-600 border-gray-300"
                          />
                        ))}
                      </div>
                    )}
                </span>
                <IconButton
                  size="small"
                  onClick={e => {
                    e.stopPropagation();
                    setShowRawEvents(!showRawEvents);
                  }}
                  className="ml-2"
                >
                  <ExpandMore
                    sx={{
                      transform: showRawEvents ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s',
                    }}
                  />
                </IconButton>
              </div>

              <Collapse in={showRawEvents}>
                <div className="mt-4">
                  {isLoadingAnalytics ? (
                    <div className="flex items-center justify-center py-8">
                      <CircularProgress size={24} />
                      <span className="ml-3 text-sm text-gray-500">Loading raw events...</span>
                    </div>
                  ) : analyticsData.analytics.raw_events &&
                    analyticsData.analytics.raw_events.length > 0 ? (
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {analyticsData.analytics.raw_events.map((event: any, index: number) => (
                        <RawEventCard key={index} event={event} index={index} />
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500 py-4">
                      No raw events available for this encounter.
                    </div>
                  )}
                </div>
              </Collapse>
            </div>
          )}
        </div>
      </CardContent>
      {/* Section Details Modal */}
      <SectionDetailsModal
        open={selectedSection !== null}
        onClose={() => setSelectedSection(null)}
        sectionDetails={selectedSection}
        rawEvents={analyticsData?.analytics?.raw_events}
        isLoading={isLoadingAnalytics}
      />
    </Card>
  );
}
